import React, { useEffect } from 'react';
import { useFormik, FormikValues } from 'formik';
import moment from 'moment';
import styled from 'styled-components';
import { Constants, Validators } from 'core';
import { Profil, RequestError, ServiceType, UserAlreadyExistCodeError, UserParams } from 'types';
import { useDispatch } from 'react-redux';
import { path } from 'ramda';

import Input from 'components/organisms/input';
import Button from 'components/molecules/button';
import FormError from 'components/molecules/form-error';
import { ClearStateSliceActions } from 'common/config/store';
import { useIntl } from 'common/config/i18n';
import InputSelect from 'components/organisms/select';

interface Props {
  onCreate: (values: FormikValues) => void;
  error?: RequestError;
  status?: 'loading' | 'error' | 'success';
  userCodeError?: UserAlreadyExistCodeError;
  profils: Profil[];
  serviceTypes: ServiceType[];
}

const CreationDate = styled.p``;

const WrapperForm = styled.header`
  margin-bottom: 20px;
`;
const WrapperInput = styled.div`
  margin-top: 20px;
`;
const UpLabel = styled.div`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: bold;
`;

const Form = (props: Props) => {
  const { t } = useIntl();
  const dispatch = useDispatch();

  const initialValues: UserParams = {
    firstName: '',
    userName: '',
    profileID: undefined,
    trainTypeIDs: props.serviceTypes.length === 1 ? [props.serviceTypes[0].id] : undefined,
    groupName: '',
    lastName: '',
    email: '',
  };
  const formik = useFormik({
    initialValues,
    validate: (values) => Validators.createUser(values),
    onSubmit: (values) => {
      props.onCreate(values);
    },
  });

  useEffect(() => {
    dispatch(ClearStateSliceActions.clearStateSlice({ sliceReducer: 'User' }));
  }, [dispatch]);

  const selectProfilsData = [
    {
      id: 'profils',
      list:
        props?.profils?.map((profil) => ({
          id: profil.id,
          name: profil.name,
        })) || [],
    },
  ];

  const selectServiceTypesData = [
    {
      id: 'serviceTypes',
      list:
        props?.serviceTypes?.map((serviceType) => ({
          id: serviceType.id,
          name: serviceType.serviceTypeName,
        })) || [],
    },
  ];

  return (
    <form onSubmit={formik.handleSubmit}>
      <CreationDate>{`${t('form', 'FORM_CREATION_DATE')} ${moment().format('DD/MM/YYYY')}`}</CreationDate>

      <WrapperForm>
        <WrapperInput>
          <Input
            width="300px"
            upLabel={t('form', 'FORM_USER_NAME')}
            name="lastName"
            onChange={formik.handleChange}
            value={formik.values.lastName}
          />
        </WrapperInput>
        <FormError formik={formik} fieldName="lastName" />

        <WrapperInput>
          <Input
            width="300px"
            upLabel={t('form', 'FORM_USER_SURNAME')}
            name="firstName"
            onChange={formik.handleChange}
            value={formik.values.firstName}
          />
        </WrapperInput>
        <FormError formik={formik} fieldName="firstName" />

        <WrapperInput>
          <Input
            width="300px"
            upLabel={t('form', 'FORM_USERNAME')}
            name="userName"
            onChange={formik.handleChange}
            value={formik.values.userName}
          />
        </WrapperInput>
        <FormError formik={formik} fieldName="userName" />

        <WrapperInput>
          <UpLabel>{t('form', 'FORM_USER_PROFIL')}</UpLabel>
          <InputSelect
            onChange={(id) => formik.setFieldValue('profileID', id)}
            defaultValue={formik.values.profileID}
            data={selectProfilsData}
            style={{ width: '300px', borderRadius: '5px' }}
          />
        </WrapperInput>
        <FormError formik={formik} fieldName="profileID" />

        <WrapperInput>
          <UpLabel>{t('form', 'FORM_USER_CIRCULATION_TYPE')}</UpLabel>
          <InputSelect
            mode="multiple"
            onChange={(trainTypeIDs) => formik.setFieldValue('trainTypeIDs', trainTypeIDs)}
            defaultValue={formik.values.trainTypeIDs}
            data={selectServiceTypesData}
            style={{ width: '300px', borderRadius: '5px' }}
          />
        </WrapperInput>
        <FormError formik={formik} fieldName="trainTypeIDs" />

        <WrapperInput>
          <Input
            width="300px"
            upLabel={t('form', 'FORM_USER_GROUP')}
            name="groupName"
            onChange={formik.handleChange}
            value={formik.values.groupName}
          />
        </WrapperInput>
        <FormError formik={formik} fieldName="groupName" />

        <WrapperInput>
          <Input
            width="300px"
            upLabel={t('form', 'FORM_EMAIL')}
            type="email"
            name="email"
            onChange={formik.handleChange}
            value={formik.values.email}
          />
        </WrapperInput>
        <FormError formik={formik} fieldName="email" />
      </WrapperForm>

      {props?.error === Constants.ALREADY_EXISTED_PROBLEM_CODE &&
        props?.userCodeError?.includes(Constants.USER_EMAIL_ALREADY_EXISTED_CODE_ERROR) && (
          <FormError message={t('form', 'FORM_USER_EMAIL_ALLREADY_EXIST')} />
        )}
      {props?.error === Constants.ALREADY_EXISTED_PROBLEM_CODE &&
        props?.userCodeError?.includes(Constants.USER_USERNAME_ALREADY_EXISTED_CODE_ERROR) && (
          <FormError message={t('form', 'FORM_USER_USERNAME_ALLREADY_EXIST')} />
        )}
      {props?.error && props?.error !== Constants.ALREADY_EXISTED_PROBLEM_CODE && (
        <FormError message={t('form', 'FORM_UNKNOWN_ERROR')} />
      )}

      <Button
        width="100%"
        borderRadius="20px"
        disabled={
          !!(formik.errors.userName && formik.touched.userName) ||
          !!(formik.errors.firstName && formik.touched.firstName) ||
          !!(formik.errors.profileID && formik.touched.profileID) ||
          !!(formik.errors.trainTypeIDs && formik.touched.trainTypeIDs) ||
          !!(formik.errors.groupName && formik.touched.groupName) ||
          !!(formik.errors.lastName && formik.touched.lastName) ||
          !!(formik.errors.email && formik.touched.email)
        }
        loading={props.status === 'loading'}
        htmlType="submit"
      >
        {t('Buttons', 'CREATE_BUTTON')}
      </Button>
    </form>
  );
};
export default Form;
